import { useEffect, useLayoutEffect, useMemo } from 'react';

import { useApi } from 'contexts/ApiContext';

import { usePathname, useRouter } from '@polyai/common/hooks/useRouter';

import { projectInit } from 'actions';
import api from 'api';
import { useLocalDispatch } from 'hooks/reduxHooks';
import useAccount from 'hooks/useAccount';
import useAnalytics from 'hooks/useAnalytics/useAnalytics';
import useFeatureSwitch from 'hooks/useFeatureSwitch/useFeatureSwitch';
import useProject from 'hooks/useProject';
import { useProjectResources } from 'hooks/useProjectResources';
import useSampleProject from 'hooks/useSampleProjects/useSampleProject';

const useJupiterSettings = () => {
  const { isFeatureOn } = useFeatureSwitch();
  const isSampleProjectEnabled = isFeatureOn('sample_projects');
  const { inSampleProject, isLoadingSampleProject, didSampleProjectError } =
    useSampleProject(isSampleProjectEnabled);
  const { changeMethods } = useApi();
  const { replaceUrl } = useRouter();
  const pathname = usePathname();

  const {
    account,
    isLoading: isLoadingAccount,
    isError: isAccountError,
  } = useAccount();

  const {
    project,
    isLoadingProject,
    isProjectError,
    isProjectForbidden,
    projectError,
  } = useProject();

  const {
    isFetchingProjectFeatures,
    isProjectReadOnly,
    didProjectFeaturesError,
  } = useProjectResources();

  useAnalytics();

  const isLoading = inSampleProject
    ? isLoadingSampleProject
    : isLoadingAccount || isLoadingProject || isFetchingProjectFeatures;

  const { isNotFound, isError, isProjectInAnotherAccount } = useMemo(() => {
    if (inSampleProject) {
      return {
        isNotFound: false,
        isError: didSampleProjectError,
        isProjectInAnotherAccount: false,
      };
    }

    const isProjectInAccount =
      account?.active && project?.genai_account_id === account.id;

    // Project genuinely doesn't exist (404) or doesn't belong to this account
    const isNotFound =
      (isProjectError && projectError?.httpStatus === 404) ||
      (!isProjectInAccount && !isAccountError && !isProjectError);

    // Other errors (network failures, 500s, etc.)
    const isError =
      isAccountError ||
      (isProjectError && !isNotFound) ||
      !isProjectInAccount ||
      didProjectFeaturesError;

    return {
      isNotFound,
      isError,
      isProjectInAnotherAccount: !isProjectInAccount,
    };
  }, [
    inSampleProject,
    didSampleProjectError,
    account,
    project,
    isAccountError,
    isProjectError,
    projectError?.httpStatus,
    didProjectFeaturesError,
  ]);

  const isForbidden = inSampleProject
    ? !isSampleProjectEnabled
    : isProjectForbidden;
  const isReadOnly = inSampleProject ? true : isProjectReadOnly;

  useEffect(() => {
    if (isProjectInAnotherAccount && project?.genai_account_id && account?.id) {
      replaceUrl(pathname.replace(account.id, project.genai_account_id));
    }
  }, [
    account?.id,
    isProjectInAnotherAccount,
    pathname,
    project?.genai_account_id,
    replaceUrl,
  ]);

  useEffect(() => {
    if (inSampleProject) {
      changeMethods({
        startChat: api.startTemplateChat,
        respond: api.templateRespond,
        endChat: api.endTemplateChat,
      });
    }
  }, [inSampleProject, changeMethods]);

  return {
    project,
    isLoading,
    isNotFound,
    isError,
    isForbidden,
    isReadOnly,
    inSampleProject, // FIXME: remove when chat fixed
  };
};

const useProjectInit = () => {
  const dispatch = useLocalDispatch();

  useLayoutEffect(
    () => () => {
      dispatch(projectInit());
    },
    [dispatch],
  );

  return useJupiterSettings();
};

export default useProjectInit;
